import React, { useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { KeyRound } from "lucide-react";

export const ChangePasswordCard = () => {
  const { user } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const googleOnly = user?.auth_provider === "google";

  const submit = async () => {
    if (next !== confirm) return toast.error("The two new passwords do not match");
    if (next.length < 8) return toast.error("Use at least 8 characters");
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      setCurrent(""); setNext(""); setConfirm("");
      toast.success("Password changed. Other devices have been signed out.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <Card className="border-border" data-testid="change-password-card">
      <CardContent className="p-6 lg:p-8">
        <h3 className="font-display text-2xl mb-1 flex items-center gap-2"><KeyRound size={18}/> Password</h3>
        {googleOnly ? (
          <p className="text-sm text-muted-foreground" data-testid="change-password-google-note">
            You sign in with Google, so there is no password on this account. Manage it from your Google account.
          </p>
        ) : (
          <>
            <p className="text-sm text-muted-foreground mb-6">
              Choose something only you know. Changing it signs out every other device.
            </p>
            <div className="grid sm:grid-cols-3 gap-4">
              <div>
                <Label className="overline">Current password</Label>
                <Input type="password" value={current} onChange={(e)=>setCurrent(e.target.value)} className="mt-2 bg-transparent" data-testid="pwd-current"/>
              </div>
              <div>
                <Label className="overline">New password</Label>
                <Input type="password" value={next} onChange={(e)=>setNext(e.target.value)} className="mt-2 bg-transparent" data-testid="pwd-new"/>
              </div>
              <div>
                <Label className="overline">Repeat new password</Label>
                <Input type="password" value={confirm} onChange={(e)=>setConfirm(e.target.value)} className="mt-2 bg-transparent" data-testid="pwd-confirm"/>
              </div>
            </div>
            <Button
              onClick={submit}
              disabled={busy || !current || !next}
              className="btn-pill bg-primary text-primary-foreground mt-6"
              data-testid="pwd-save"
            >
              {busy ? "Updating…" : "Change Password"}
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default ChangePasswordCard;
